/**
 * Tu sesión — consume `GET /api/v1/me`.
 *
 * `OD-0017 D-03` exige SEIS estados en todo componente de datos:
 * CARGANDO, VACÍO, CON DATOS, ERROR, SIN PERMISO y LÍMITE ALCANZADO.
 * Aquí están los seis. Dos matices honestos:
 *
 *   - VACÍO no puede ocurrir hoy con `/me` (una sesión válida siempre trae
 *     organización y usuario); se implementa igual, porque un componente que
 *     no los tiene todos está incompleto, no "pendiente de pulir".
 *   - LÍMITE ALCANZADO se muestra si la API responde 429. El texto completo
 *     que exige `URS §110-113` —límite, consumo, restante, reset, acciones y
 *     upgrade— necesita datos que esta API todavía no da: cuando existan,
 *     se completa aquí.
 */
import { useState } from "react";
import { api, type Sesion } from "../api/cliente";
import { t } from "../i18n/es";
import { Aviso, Boton, Tarjeta } from "../ui/primitivas";

export type EstadoSesion =
  | { readonly tipo: "cargando" }
  | { readonly tipo: "datos"; readonly sesion: Sesion }
  | { readonly tipo: "vacio" }
  | { readonly tipo: "error" }
  | { readonly tipo: "sin-permiso" }
  | { readonly tipo: "limite" };

export function PantallaSesion({
  estado,
  onRecargar,
  onSalir,
}: {
  estado: EstadoSesion;
  onRecargar: () => void;
  onSalir: () => void;
}) {
  const [saliendo, setSaliendo] = useState(false);

  async function salir(): Promise<void> {
    setSaliendo(true);
    await api.logout();
    setSaliendo(false);
    onSalir();
  }

  return (
    <Tarjeta titulo={t.sesion.titulo}>
      {estado.tipo === "cargando" ? <p>{t.sesion.cargando}</p> : null}

      {estado.tipo === "error" ? (
        <>
          <Aviso tono="error">{t.sesion.error}</Aviso>
          <Boton variante="secundario" onClick={onRecargar}>
            {t.sesion.reintentar}
          </Boton>
        </>
      ) : null}

      {estado.tipo === "sin-permiso" ? <Aviso tono="aviso">{t.sesion.sinPermiso}</Aviso> : null}
      {estado.tipo === "limite" ? <Aviso tono="aviso">{t.sesion.limite}</Aviso> : null}
      {estado.tipo === "vacio" ? <Aviso tono="info">{t.sesion.vacio}</Aviso> : null}

      {estado.tipo === "datos" ? (
        <dl className="datos">
          <dt>{t.sesion.organizacion}</dt>
          <dd>{estado.sesion.organizationId}</dd>
          <dt>{t.sesion.usuario}</dt>
          <dd>{estado.sesion.userId}</dd>
          <dt>{t.sesion.capacidades}</dt>
          <dd>
            <ul className="lista-capacidades">
              {estado.sesion.capabilities.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </dd>
        </dl>
      ) : null}

      <Boton variante="secundario" onClick={() => void salir()} deshabilitado={saliendo}>
        {saliendo ? t.sesion.saliendo : t.sesion.salir}
      </Boton>
    </Tarjeta>
  );
}
